import React, { useCallback, useEffect, useState } from "react";
import { Container, Row } from "reactstrap";
import NavBar from "../NavBar";
import MentorshipBanner from "./MentorshipBanner";
import MentorshipStats from "./MentorshipStats";
import MentorshipFilters from "./MentorshipFilters";
import MentorshipGrid from "./MentorshipGrid";
import MentorshipRequests from "./MentorshipRequests";
import BecomeMentorCard from "./BecomeMentorCard";
import MentorshipAdCard from "./MentorshipAdCard";
import CreateMentorship from "./CreateMentorship";
import SuccessMemeberModal from "./SuccessMemeberModal";
import ApplyMentorShip from "./ApplyMentorShip";
import MentorshipApplicationModal from "./MentorshipApplicationModal";
import MentorShipApproved from "./MentorShipApproved";
import MentorSetting from "./MentorSetting";
import MentorShipSchedule from "./MentorShipSchedule";
import { DEFAULT_MENTOR_AVATAR as placeholderAvatar } from "../../utils/mentorshipUtils";
import { getCategoryNameById } from "./data/mentorshipData";
import MentorshipSEO from "./MentorshipSEO";
import "./mentorship.css";
import "../agenda/AgendaFoundation.css";

const MENTOR_ACCOUNT_NAME = "Dr. Jane Smith";

function formatSubmittedDate(date) {
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function Mentorship() {
  const [activeCategoryId, setActiveCategoryId] = useState(0);
  const [isMentorFormOpen, setIsMentorFormOpen] = useState(false);
  const [mentorFormMode, setMentorFormMode] = useState("create");
  const [mentorFormData, setMentorFormData] = useState(null);
  const [isMentorSuccessOpen, setIsMentorSuccessOpen] = useState(false);
  const [mentorApplication, setMentorApplication] = useState(null);
  const [applyMentorTarget, setApplyMentorTarget] = useState(null);
  const [isApplyMentorOpen, setIsApplyMentorOpen] = useState(false);
  const [isApplicationFormOpen, setIsApplicationFormOpen] = useState(false);
  const [bookedMentorIds, setBookedMentorIds] = useState([]);
  const [isMentorApprovedOpen, setIsMentorApprovedOpen] = useState(false);
  const [isOwnProfileOpen, setIsOwnProfileOpen] = useState(false);
  const [isMentorSettingOpen, setIsMentorSettingOpen] = useState(false);
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [scheduleTarget, setScheduleTarget] = useState(null);

  // Set by Login.jsx on a repeat Doctor login, or by syncMentorApprovalFlag
  // (services/api.js) whenever splash data resolves and reveals a fresh
  // approval mid-session. Checked once on mount for the case where the flag
  // was already set before this page loaded, AND on mrMentorStatusSynced
  // for the case where the doctor was already sitting on this page when the
  // approval came through — that event fires asynchronously (after a splash
  // fetch resolves), well after this mount-only effect would have already
  // run and found nothing.
  useEffect(() => {
    const checkApprovalFlag = () => {
      if (localStorage.getItem("mr_show_mentorship_approved") === "true") {
        localStorage.removeItem("mr_show_mentorship_approved");
        setIsMentorApprovedOpen(true);
      }
    };
    checkApprovalFlag();
    window.addEventListener("mrMentorStatusSynced", checkApprovalFlag);
    return () =>
      window.removeEventListener("mrMentorStatusSynced", checkApprovalFlag);
  }, []);

  const toggleMentorApproved = useCallback(() => {
    setIsMentorApprovedOpen((prev) => !prev);
  }, []);

  const handleSelectCategory = useCallback((categoryId) => {
    setActiveCategoryId(categoryId);
  }, []);

  // "View Profile" opens the same ApplyMentorShip preview modal as "Apply
  // for Mentorship" — it's a full profile look with an Apply action inside,
  // not a separate profile page.
  const handleViewProfile = useCallback((mentor) => {
    setApplyMentorTarget(mentor);
    setIsApplyMentorOpen(true);
  }, []);

  const handleApply = useCallback((mentor) => {
    setApplyMentorTarget(mentor);
    setIsApplyMentorOpen(true);
  }, []);

  const toggleApplyMentor = useCallback(() => {
    setIsApplyMentorOpen((prev) => !prev);
  }, []);

  const handleOpenApplicationForm = useCallback((mentor) => {
    setApplyMentorTarget(mentor);
    setIsApplicationFormOpen(true);
  }, []);

  const toggleApplicationForm = useCallback(() => {
    setIsApplicationFormOpen((prev) => !prev);
  }, []);

  const handleMentorBooked = useCallback((bookedMentor) => {
    setBookedMentorIds((prev) =>
      prev.includes(bookedMentor.id) ? prev : [...prev, bookedMentor.id],
    );
  }, []);

  const handleBecomeMentor = useCallback(() => {
    setMentorFormMode("create");
    setMentorFormData(null);
    setIsMentorFormOpen(true);
  }, []);

  const toggleMentorForm = useCallback(() => {
    setIsMentorFormOpen((prev) => !prev);
  }, []);

  const handleMentorSubmitted = useCallback((data, mode) => {
    setMentorFormData(data);
    if (mode === "create") {
      setMentorApplication({
        image: placeholderAvatar,
        name: MENTOR_ACCOUNT_NAME,
        specialty: data.specialty || "Ophthalmology",
        submittedOn: formatSubmittedDate(new Date()),
      });
      setIsMentorSuccessOpen(true);
    } else {
      setMentorApplication((prev) =>
        prev ? { ...prev, specialty: data.specialty || prev.specialty } : prev,
      );
    }
  }, []);

  const handleCloseMentorSuccess = useCallback(() => {
    setIsMentorSuccessOpen(false);
  }, []);

  const handleEditFromSuccess = useCallback(() => {
    setIsMentorSuccessOpen(false);
    setMentorFormMode("edit");
    setIsMentorFormOpen(true);
  }, []);

  const handleEditMentorApplication = useCallback(() => {
    setMentorFormMode("edit");
    setIsMentorFormOpen(true);
  }, []);

  const handleViewOwnProfile = useCallback(() => {
    setIsOwnProfileOpen(true);
  }, []);

  const toggleOwnProfile = useCallback(() => {
    setIsOwnProfileOpen((prev) => !prev);
  }, []);

  const handleOpenMentorSettings = useCallback(() => {
    setIsMentorSettingOpen(true);
  }, []);

  const toggleMentorSetting = useCallback(() => {
    setIsMentorSettingOpen((prev) => !prev);
  }, []);

  const handleScheduleSession = useCallback((request) => {
    setScheduleTarget(request);
    setIsScheduleOpen(true);
  }, []);

  const toggleSchedule = useCallback(() => {
    setIsScheduleOpen((prev) => !prev);
  }, []);

  const ownMentorProfile = {
    id: JSON.parse(localStorage.getItem("SplashData") || "null")?.user?.id,
    image: mentorApplication?.image || placeholderAvatar,
    name: mentorApplication?.name || MENTOR_ACCOUNT_NAME,
    specialty: mentorApplication?.specialty || "Ophthalmology",
    bio:
      mentorFormData?.bio ||
      "Cornea and ocular surface specialist. Reference for complex dry eye and surface reconstruction.",
    institution: "MEDICAL RADAR CLINIC",
    institutionSub: "Ophthalmology Reference Center",
    location: "Spain",
    experience: "10+ years",
    focusAreas: mentorFormData?.focusList?.length
      ? mentorFormData.focusList
      : [
          "Cornea & ocular surface disease",
          "Complex dry eye management",
          "Corneal transplantation",
          "Ocular surface reconstruction",
        ],
  };

  const selectedCategoryName = getCategoryNameById(activeCategoryId);

  return (
    <Container
      fluid
      className="mentorship-page px-0"
    >
      <MentorshipSEO selectedCategoryName={selectedCategoryName} />
      <Row className="m-0">
        <NavBar />
      </Row>

      <div className="mentorship-main-layout feed-aligned-page-layout">
          <div className="d-xl-block d-none mentorship-sidebar">
            <div className="mentorship-sidebar-panel">
              <MentorshipRequests onScheduleSession={handleScheduleSession} />
              <BecomeMentorCard
                onApply={handleBecomeMentor}
                onEdit={handleEditMentorApplication}
                onManageProfile={handleViewOwnProfile}
                onOpenSettings={handleOpenMentorSettings}
                mentorApplication={mentorApplication}
              />
            </div>

            <div className="mentorship-sidebar-panel--sticky">
              <MentorshipAdCard />
            </div>
          </div>

          {/* Main Content */}
          <div className="d-xl-block d-none mentorship-content-area">
            <MentorshipBanner />
            <MentorshipStats />
            <MentorshipFilters
              activeCategoryId={activeCategoryId}
              onSelect={handleSelectCategory}
            />

            <MentorshipGrid
              activeCategoryId={activeCategoryId}
              onViewProfile={handleViewProfile}
              onApply={handleApply}
            />
          </div>

          <div
            className="d-xl-none w-100"
            style={{ paddingBottom: "2rem" }}
          >
            <MentorshipRequests onScheduleSession={handleScheduleSession} />
            <BecomeMentorCard
              onApply={handleBecomeMentor}
              onEdit={handleEditMentorApplication}
              onManageProfile={handleViewOwnProfile}
              onOpenSettings={handleOpenMentorSettings}
              mentorApplication={mentorApplication}
            />
            <MentorshipStats />
            <MentorshipGrid
              activeCategoryId={activeCategoryId}
              onViewProfile={handleViewProfile}
              onApply={handleApply}
            />
            <MentorshipAdCard />
          </div>
      </div>

      <CreateMentorship
        isOpen={isMentorFormOpen}
        toggle={toggleMentorForm}
        mode={mentorFormMode}
        initialData={mentorFormData}
        onSubmitted={handleMentorSubmitted}
      />
      <SuccessMemeberModal
        show={isMentorSuccessOpen}
        onHide={handleCloseMentorSuccess}
        onGotIt={handleCloseMentorSuccess}
        onEdit={handleEditFromSuccess}
      />
      <ApplyMentorShip
        isOpen={isApplyMentorOpen}
        toggle={toggleApplyMentor}
        mentor={applyMentorTarget}
        onApply={handleOpenApplicationForm}
        isBooked={
          !!applyMentorTarget && bookedMentorIds.includes(applyMentorTarget.id)
        }
      />
      <MentorshipApplicationModal
        isOpen={isApplicationFormOpen}
        toggle={toggleApplicationForm}
        mentor={applyMentorTarget}
        onSubmitted={handleMentorBooked}
      />
      <MentorShipApproved
        isOpen={isMentorApprovedOpen}
        toggle={toggleMentorApproved}
      />
      <ApplyMentorShip
        isOpen={isOwnProfileOpen}
        toggle={toggleOwnProfile}
        mentor={ownMentorProfile}
        mode="own"
        onManage={handleEditMentorApplication}
      />
      <MentorSetting
        isOpen={isMentorSettingOpen}
        toggle={toggleMentorSetting}
      />
      <MentorShipSchedule
        isOpen={isScheduleOpen}
        toggle={toggleSchedule}
        mentee={scheduleTarget}
      />
    </Container>
  );
}

export default React.memo(Mentorship);
